import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import ProtectedRoute from '../components/ProtectedRoute';
import PageTransition from '../components/PageTransition';

// Lazy load components
const Header = lazy(() => import('../module/layouts/include/header'));
const Footer = lazy(() => import('../module/layouts/include/footer'));
const Register = lazy(() => import('../module/auth/Register'));
const Login = lazy(() => import('../module/auth/Login'));
const Home = lazy(() => import('../module/pages/Home/Home'));

const ServicesPage = lazy(() => import('../module/pages/Service/Services'));
const TermsPage = lazy(() => import('../module/pages/Terms/TermsPage'));
const BlogPage = lazy(() => import('../module/pages/Blog/BlogPage'));
const BlogPostPage = lazy(() => import('../module/pages/Blog/BlogPostPage'));
const SupportPage = lazy(() => import('../module/pages/Support/SupportPage'));
const AboutPage = lazy(() => import('../module/pages/About/AboutPage'));
const PrivacyPolicyPage = lazy(() => import('../module/pages/Privacy/PrivacyPolicyPage'));
const ContactPage = lazy(() => import('../module/pages/Contact/ContactPage'));

// Admin
const AdminLogin = lazy(() => import('../module/admin/AdminLogin'));
const AdminLayout = lazy(() => import('../module/admin/AdminLayout'));
const Dashboard = lazy(() => import('../module/admin/pages/Dashboard'));
const PagesAdmin = lazy(() => import('../module/admin/pages/PagesAdmin'));
const ServicesAdmin = lazy(() => import('../module/admin/pages/ServicesAdmin'));
const BlogAdmin = lazy(() => import('../module/admin/pages/BlogAdmin'));
const TestimonialsAdmin = lazy(() => import('../module/admin/pages/TestimonialsAdmin'));
const FaqsAdmin = lazy(() => import('../module/admin/pages/FaqsAdmin'));
const PortfolioAdmin = lazy(() => import('../module/admin/pages/PortfolioAdmin'));
const SettingsAdmin = lazy(() => import('../module/admin/pages/SettingsAdmin'));
const UsersAdmin = lazy(() => import('../module/admin/pages/UsersAdmin'));
const ContactMessagesAdmin = lazy(() => import('../module/admin/pages/ContactMessagesAdmin'));

// Layout wrapper
const Layout = ({ children }) => (
  <>
    <Suspense fallback={<div style={{ height: '76px' }} />}>
      <Header />
    </Suspense>

    <main>
      <PageTransition>{children}</PageTransition>
    </main>

    <Suspense fallback={<div style={{ height: '320px' }} />}>
      <Footer />
    </Suspense>
  </>
);

function AppRoutes() {
  return (
    <Router>
      <Suspense
        fallback={
          <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        }
      >
        <Routes>

          {/* Public Auth */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />

          {/* Main Pages */}
          <Route path="/" element={<Layout><Home /></Layout>} />
          <Route path="/about" element={<Layout><AboutPage /></Layout>} />
          <Route path="/blog" element={<Layout><BlogPage /></Layout>} />
          <Route path="/blog/:slug" element={<Layout><BlogPostPage /></Layout>} />
          <Route path="/services" element={<Layout><ServicesPage /></Layout>} />
          <Route path="/support" element={<Layout><SupportPage /></Layout>} />
          <Route path="/terms" element={<Layout><TermsPage /></Layout>} />
          <Route path="/privacy-policy" element={<Layout><PrivacyPolicyPage /></Layout>} />
          <Route path="/contact" element={<Layout><ContactPage /></Layout>} />

          {/* Admin */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute requireAdmin>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="pages" element={<PagesAdmin />} />
            <Route path="services" element={<ServicesAdmin />} />
            <Route path="blog" element={<BlogAdmin />} />
            <Route path="testimonials" element={<TestimonialsAdmin />} />
            <Route path="faqs" element={<FaqsAdmin />} />
            <Route path="portfolio" element={<PortfolioAdmin />} />
            <Route path="settings" element={<SettingsAdmin />} />
            <Route path="users" element={<UsersAdmin />} />
            <Route path="messages" element={<ContactMessagesAdmin />} />
          </Route>

        </Routes>
      </Suspense>
    </Router>
  );
}

export default AppRoutes;
